import { useEffect } from "react";
import { Link, Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  ArrowLeft,
  BarChart3,
  CalendarDays,
  Inbox,
  Layers,
  MessageSquare,
  LogOut,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const LINKS = [
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/visits", label: "Visits", icon: CalendarDays },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/content", label: "Content", icon: Layers },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  // Redirect /admin → /admin/leads
  useEffect(() => {
    if (user && location.pathname === "/admin") {
      navigate("/admin/leads", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  if (loading || !user) {
    return (
      <main className="min-h-screen grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
      </main>
    );
  }

  if (!user.is_admin) {
    return (
      <main
        className="min-h-screen grid place-items-center bg-[rgb(var(--page))]"
        data-testid="admin-restricted"
      >
        <div className="text-center max-w-sm px-6">
          <h1 className="font-display font-black text-2xl text-ink">
            Access restricted
          </h1>
          <p className="mt-2 text-sm text-muted-ink">
            The account <span className="font-bold text-ink">{user.email}</span>{" "}
            is not authorised for this admin panel.
          </p>
          <div className="mt-5 flex gap-2 justify-center">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => navigate("/")}
              data-testid="restricted-back-home"
            >
              Back to site
            </Button>
            <Button
              className="rounded-full bg-ink text-white hover:bg-slate-800"
              onClick={async () => {
                await logout();
                navigate("/login", { replace: true });
              }}
              data-testid="restricted-switch-account"
            >
              Switch account
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[rgb(var(--page))]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm font-bold text-muted-ink hover:text-ink"
              data-testid="admin-back-home"
            >
              <ArrowLeft className="h-4 w-4" /> Back to site
            </Link>
            <h1 className="mt-2 font-display font-black text-3xl md:text-4xl text-ink">
              Admin
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name || user.email}
                className="h-9 w-9 rounded-full object-cover hairline"
              />
            ) : null}
            <div className="text-right leading-tight hidden sm:block">
              <div className="text-sm font-bold text-ink">
                {user?.name || "Admin"}
              </div>
              <div className="text-[11px] text-muted-ink">{user?.email}</div>
            </div>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={async () => {
                await logout();
                navigate("/login", { replace: true });
              }}
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>

        {/* Tabs / sidebar */}
        <nav
          className="mt-6 flex gap-1 overflow-x-auto p-1 rounded-full hairline bg-white w-fit"
          data-testid="admin-nav"
        >
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`admin-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                  isActive
                    ? "bg-ink text-white"
                    : "text-ink-2 hover:bg-orange-50 hover:text-orange-700"
                }`
              }
            >
              <l.icon className="h-4 w-4" />
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Content */}
        <div className="mt-6">
          <Outlet />
        </div>
      </div>
    </main>
  );
}
